import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CasesAndCounts, CaseStatus } from "../models";
import { fetchCases } from "../api";
import { Resource } from "../types";
import { RootState } from "../store";
import { castDraft } from "immer";
import { loadFiltersFromSession, saveFiltersToSession, SessionFilterKeys } from "../storage";

export interface CaseFilters {
  readonly procId?: string;
  readonly status?: CaseStatus;
  readonly assignee?: string;
}

export interface CasesState {
  readonly cases: Resource<CasesAndCounts>;
  readonly filters: CaseFilters;
}

export const initialState: CasesState = {
  cases: {
    isPending: false
  },
  filters: loadFiltersFromSession<CaseFilters>(SessionFilterKeys.Case) ?? {}
};

// thunks
export const casesFetch = createAsyncThunk(
  "cases/casesFetch",
  async (studyId: string, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    const response = await fetchCases(
      studyId,
      state.cases.filters.procId,
      state.cases.filters.status,
      state.cases.filters.assignee
    );
    return response;
  }
);

export const setCaseFiltersAndFetch = createAsyncThunk(
  "cases/setCaseFilters",
  async (filters: { studyId: string; caseFilters: CaseFilters }, thunkApi) => {
    const { dispatch } = thunkApi;
    dispatch(setFilters(filters.caseFilters));
    await dispatch(casesFetch(filters.studyId));
  }
);

export const casesSlice = createSlice({
  name: "cases",
  initialState: initialState,
  reducers: {
    clearCaseFilters: state => {
      state.filters = {};
      saveFiltersToSession<CaseFilters>(SessionFilterKeys.Case, {});
    },
    setFilters: (state, action: PayloadAction<CaseFilters>) => {
      state.filters = action.payload;
      saveFiltersToSession<CaseFilters>(SessionFilterKeys.Case, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(casesFetch.pending, state => {
      state.cases = { isPending: true };
    });
    builder.addCase(casesFetch.fulfilled, (state, action) => {
      state.cases = { resource: castDraft(action.payload) };
    });
    builder.addCase(casesFetch.rejected, (state, action) => {
      state.cases = { errorMessage: action.error.message || "" };
    });
  }
});

export const { clearCaseFilters, setFilters } = casesSlice.actions;
export default casesSlice.reducer;
